import { action, computed, observable, isComputed } from 'mobx';

import { Model } from 'core/model';
import { AppBase, AppFactory } from 'core/app';
import RefMap from 'libs/RefMap';

const unmapComputeds = arrayLike =>
  arrayLike
    .filter(element => !!element)
    .map(element => (isComputed(element) ? element.get() : element));

const wrapInApps = (data, elementFactory, ElementApp, updateCache) =>
  data.map(element => {
    if (element instanceof AppBase || isComputed(element)) return element;
    if (!(element instanceof Model)) {
      if (updateCache) {
        const app = new ElementApp(this, element, false, false);
        app.expandValues(updateCache);
        return app;
      }
      return new ElementApp(this, element);
    }
    return elementFactory(element);
  });

// Wraps around and an array of models
export class ArrayApp extends AppBase {
  static registryName = 'ArrayApp';
  @observable upstream = [];

  constructor(
    parent = null,
    initial = [],
    fromUpstream = false,
    shouldExpandValues = true,
  ) {
    super(parent);
    this.ElementApp = this.constructor.ElementApp;
    this.elementFactory = AppFactory(this, this.ElementApp);
    this._initial = initial;
    this._fromUpstream = fromUpstream;

    if (shouldExpandValues) {
      this.expandValues();
    }
  }

  @action expandValues(updateCache = null) {
    this.update(this._initial, this._fromUpstream, updateCache);
  }

  @computed get apps() {
    return unmapComputeds(
      this.isPropertyDirty('elements')
        ? this.locals.get('elements')
        : this.upstreamApps,
    );
  }

  @computed get upstreamApps() {
    return wrapInApps(this.upstream, this.elementFactory, this.ElementApp);
  }

  set apps(data) {
    this.update(data);
  }

  @computed get length() {
    return this.apps.length;
  }

  @action update = (data, fromUpstream = false, updateCache) => {
    if (fromUpstream) {
      this.locals.clear();
      this.upstream = data;
    } else {
      this.locals.set(
        'elements',
        wrapInApps(data, this.elementFactory, this.ElementApp, updateCache),
      );
    }
  };

  setUpstream = upstream => {
    this.update(upstream, true);
  };

  [Symbol.iterator]() {
    return this.apps[Symbol.iterator]();
  }

  @action ResetLocals = (props, idCache) => {
    const resetCache = idCache || {};
    this.apps.forEach(app => {
      app.ResetLocals?.(props, resetCache);
    });
    if (this.isDirty) {
      this.locals.clear();
    }
  };

  getLocalApps() {
    return this.apps.slice();
  }

  push(...items) {
    const localApps = this.getLocalApps();
    localApps.push(...items);
    this.apps = localApps;
    this.fireChange();
    return this.apps.length;
  }
  /* 
  untested method 

  concat(values) {
    // values may be arrays or single values.
    // Array values are dealt with differently i.e. spread into the target.
    const localApps = this.getLocalApps();
    if (values instanceof ArrayApp) {
      // eslint-disable-next-line no-param-reassign
      values = values.apps.slice();
    }
    localApps.concat(...values);
    this.apps = localApps;
    this.fireChange();
    return this;
  }
  */

  splice(start, deleteCount, ...items) {
    const localApps = this.getLocalApps();
    const deleted = localApps.splice(start, deleteCount, ...items);
    this.apps = localApps;
    this.fireChange();
    // splice was previously returning the ArrayApp itself, but should return the deleted items array
    return deleted;
  }

  // shift/unshift?

  clear() {
    const localApps = this.getLocalApps();
    localApps.splice(0, this.apps.length);
    this.apps = localApps;
    this.fireChange();
    return this;
  }

  remove(val) {
    const localApps = this.getLocalApps();
    const itemIndex = localApps.findIndex(element => element === val);
    const deleted = this.splice(itemIndex, 1);
    this.fireChange();
    return deleted;
  }

  fireChange() {
    this.onChangeCallback?.();
  }

  onChange = callback => {
    // should be a weakRef to ensure the callback isn't registered for as long? https://v8.dev/features/weak-references
    // should also be an array of callbacks - for now we only need to register one at a time.
    this.onChangeCallback = callback;
  };

  includes(val) {
    // FIXME - currently, it will fail if the value doesn't have an id
    if (!val?.id) return false;
    return !!this.apps.findIndex(element => element.id === val.id);
  }

  some(func, thisArg = undefined) {
    return this.apps.some(func, thisArg);
  }

  filter(func) {
    return this.apps.filter(func);
  }

  map(func) {
    return this.apps.map(func);
  }

  reduce(func, initialValue) {
    return this.apps.reduce(func, initialValue);
  }

  entries() {
    return this.apps.entries();
  }

  // mobx sort and reverse return new arrays rather than modifying in place.
  sort(cmp) {
    return this.apps.sort(cmp);
  }

  reverse() {
    return this.apps.reverse();
  }

  forEach(func, thisArg = undefined) {
    return this.apps.forEach(func, thisArg);
  }

  indexOf(ele) {
    return this.apps.indexOf(ele);
  }

  slice(first, second) {
    return this.apps.slice(first, second);
  }

  peek() {
    return this.apps.peek();
  }

  find(callBack, thisArg) {
    return this.apps.find(callBack, thisArg);
  }

  findIndex(callBack, thisArg) {
    return this.apps.findIndex(callBack, thisArg);
  }

  index(i) {
    return this.apps[i];
  }

  toJs(jsCacheMap = new RefMap()) {
    if (jsCacheMap.has(this)) return jsCacheMap.get(this);
    const result = [];
    jsCacheMap.set(this, result);
    result.push(...this.apps.map(app => app.toJs(jsCacheMap)));
    return result;
  }
}
